import { compact, pick, values } from 'lodash'
import * as React from 'react'
import styled from 'styled-components'
import Button from 'web-makeup/dist/Button'
import CloudinaryImage from 'web-makeup/dist/CloudinaryImage'

import { Gen1Bot, User } from '../../typings/types'

const Icon = require('antd/lib/icon')

const StyledCloudinaryImage = styled(CloudinaryImage)`
  width: 4rem;
  height: 4rem;
`

const Entity = styled.div`
  display: grid;
  grid-template-columns: 4rem 1fr;
  grid-gap: 1rem;

  @media all and (max-width: 32rem) {
    display: none;
  }
`

const MainDescription = styled.div`
  display: grid;
  grid-template-rows: 1fr 1fr;
  align-items: center;
`

const ContactEmail = styled.span`
  color: #757575;
  font-size: 0.875rem;
`

const StyledH3 = styled.h3`
  font-size: 1.125rem;
  line-height: 1.33;
`

const BrandDetailsContainer = styled.div`
  margin: 2rem 1.5rem 0;
  grid-gap: 1.5rem;
  display: grid;
  grid-template-rows: 4rem 1fr;
  border-bottom: solid 1px #d9d9d9;
  grid-template-columns: 1fr;

  @media all and (min-width: 32rem) {
    grid-template-columns: 1fr 15rem;
  }

  @media all and (min-width: 48rem) {
    grid-template-columns: 1fr;
  }
`

const BrandContactMobile = styled.div`
  display: grid;
  grid-template-columns: ${(props: { cols: number }) => `repeat(${props.cols + 1}, 1fr)`};
  margin-bottom: 0;
  justify-content: center;
  align-items: center;
  grid-gap: 0;

  & > :not(:last-child) {
    border-right: solid 1px #d9d9d9;
  }

  @media all and (min-width: 32rem) {
    grid-template-columns: ${(props: { cols: number }) => `repeat(${props.cols}, 1fr)`};
  }

  @media all and (min-width: 48rem) {
    display: none;
  }
`

const BrandContact = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: 1.5rem;
  margin-bottom: 2rem;
  justify-content: center;
  align-items: center;

  & > :not(:last-child) {
    border-right: none;
  }

  @media all and (max-width: 48rem) {
    display: none;
  }
`

interface IconProps {
  color: '#9b9b9b' | '#4ac5e2' | '#80d135'
}

const StyledIcon = styled(Icon)`
  font-size: 2rem;
  color: ${(props: IconProps) => (props.color ? props.color : 'currentColor')};

  @media all and (min-width: 48rem) {
    display: none;
  }
`

const SkypeAnchor = styled.a`
  @media all and (max-width: 48rem) {
    display: none;
  }
`

const SkypeButton = styled(Button as any)`
  color: #3fcefc;
  border-color: #3fcefc;
  width: 100%;
`

const PhoneAnchor = styled.a`
  @media all and (max-width: 48rem) {
    display: none;
  }
`

const PhoneButton = styled(Button as any)`
  border-color: #d9d9d9;
  width: 100%;
`

const EmailButton = styled.a`
  text-align: center;

  @media all and (min-width: 32rem) {
    display: none;
  }
`

const SkypeIconButton = styled.a`
  text-align: center;

  @media all and (min-width: 48rem) {
    display: none;
  }
`

const PhoneIconButton = styled.a`
  text-align: center;

  @media all and (min-width: 48rem) {
    display: none;
  }
`

const ChatIconButton = styled.button`
  @media all and (min-width: 48rem) {
    display: none;
  }
`

interface Props {
  requester: User | Gen1Bot
}

const getColumnCount = (requester: User | Gen1Bot) => {
  return compact(values(pick(requester, ['email', 'skypeUsername', 'phoneNumber']))).length
}

const IMAGE_SIZES = {
  mobile: {
    height: 64,
    width: 64,
  },
  tablet: {
    width: 64,
    height: 64,
  },
}

const BrandContactDetails: React.SFC<Props> = ({ requester }) => (
  <BrandDetailsContainer>
    <Entity>
      <StyledCloudinaryImage
        source={
          requester.avatar && requester.avatar.cloudinaryId && requester.avatar.cloudinaryType
            ? {
                id: requester.avatar.cloudinaryId,
                type: requester.avatar.cloudinaryType,
              }
            : undefined
        }
        rounded
        sizes={IMAGE_SIZES}
      />
      <MainDescription>
        <StyledH3>{requester.name}</StyledH3>
        {'email' in requester && requester.email && <ContactEmail>{requester.email}</ContactEmail>}
      </MainDescription>
    </Entity>

    <BrandContactMobile cols={getColumnCount(requester)}>
      {'email' in requester &&
        requester.email && (
          <EmailButton href={`mailto:${requester.email}`}>
            <StyledIcon color="#9b9b9b" type="mail" />
          </EmailButton>
        )}

      {'skypeUsername' in requester &&
        requester.skypeUsername && (
          <React.Fragment>
            <SkypeIconButton href={`skype:${requester.skypeUsername}`}>
              <StyledIcon color="#4ac5e2" type="skype" />
            </SkypeIconButton>
            <SkypeAnchor href={`skype:${requester.skypeUsername}`}>
              <SkypeButton icon="skype">{requester.skypeUsername}</SkypeButton>
            </SkypeAnchor>
          </React.Fragment>
        )}

      {'phoneNumber' in requester &&
        requester.phoneNumber && (
          <React.Fragment>
            <PhoneAnchor href={`tel:${requester.phoneNumber}`}>
              <PhoneButton icon="phone">{requester.phoneNumber}</PhoneButton>
            </PhoneAnchor>
            <PhoneIconButton href={`tel:${requester.phoneNumber}`}>
              <StyledIcon color="#9b9b9b" type="phone" />
            </PhoneIconButton>
          </React.Fragment>
        )}

      <ChatIconButton>
        <StyledIcon color="#80d135" type="message" />
      </ChatIconButton>
    </BrandContactMobile>

    {(('skypeUsername' in requester && requester.skypeUsername) ||
      ('phoneNumber' in requester && requester.phoneNumber)) && (
      <BrandContact>
        {requester.skypeUsername && (
          <React.Fragment>
            <SkypeIconButton href={`skype:${requester.skypeUsername}`}>
              <StyledIcon color="#4ac5e2" type="skype" />
            </SkypeIconButton>
            <SkypeAnchor href={`skype:${requester.skypeUsername}`}>
              <SkypeButton icon="skype">{requester.skypeUsername}</SkypeButton>
            </SkypeAnchor>
          </React.Fragment>
        )}

        {requester.phoneNumber && (
          <React.Fragment>
            <PhoneAnchor href={`tel:${requester.phoneNumber}`}>
              <PhoneButton icon="phone">{requester.phoneNumber}</PhoneButton>
            </PhoneAnchor>
            <PhoneIconButton href={`tel:${requester.phoneNumber}`}>
              <StyledIcon color="#9b9b9b" type="phone" />
            </PhoneIconButton>
          </React.Fragment>
        )}
      </BrandContact>
    )}
  </BrandDetailsContainer>
)

export default BrandContactDetails



// WEBPACK FOOTER //
// ./src/components/BrandContactDetails/BrandContactDetails.tsx